import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchBrands, createBrand, deleteBrand } from '@/lib/api'
import type { Brand } from '@/lib/types'

const COUNTRY_FLAGS: Record<string, string> = {
  Germany: '🇩🇪', Japan: '🇯🇵', 'South Korea': '🇰🇷', USA: '🇺🇸', UK: '🇬🇧',
  Sweden: '🇸🇪', France: '🇫🇷', Italy: '🇮🇹', Spain: '🇪🇸', 'Czech Republic': '🇨🇿',
  Australia: '🇦🇺', China: '🇨🇳',
}

const CATEGORY_STYLES: Record<string, string> = {
  Luxury: 'bg-purple-50 text-purple-700 border-purple-200',
  SUV: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Truck: 'bg-amber-50 text-amber-700 border-amber-200',
  Commercial: 'bg-sky-50 text-sky-700 border-sky-200',
  Passenger: 'bg-slate-100 text-slate-600 border-slate-200',
}

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

export default function BrandsList() {
  const [brands, setBrands] = useState<Brand[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [newName, setNewName] = useState('')
  const [newCountry, setNewCountry] = useState('Germany')
  const [newCategory, setNewCategory] = useState('Passenger')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    try { setBrands(await fetchBrands()) } catch { /* noop */ }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function handleAdd() {
    if (!newName.trim()) return
    setSaving(true)
    setError('')
    try {
      await createBrand({ name: newName.trim(), slug: slugify(newName), country: newCountry, category: newCategory, sort_order: brands.length + 1 })
      setNewName('')
      setShowAdd(false)
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to add brand')
    }
    setSaving(false)
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Delete brand "${name}"?`)) return
    setDeleting(id)
    try { await deleteBrand(id); await load() } catch { /* noop */ }
    setDeleting(null)
  }

  const filtered = brands.filter(b =>
    !search ||
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    (b.country || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.category || '').toLowerCase().includes(search.toLowerCase())
  )

  const gradient = (i: number) => {
    const palettes = ['from-indigo-500 to-indigo-700', 'from-amber-400 to-orange-600', 'from-emerald-500 to-teal-700', 'from-purple-500 to-fuchsia-700', 'from-sky-500 to-blue-700', 'from-rose-500 to-red-700']
    return palettes[i % palettes.length]
  }

  return (
    <Layout title="Brands"
      action={<button onClick={() => setShowAdd(v => !v)} className="inline-flex items-center gap-2 bg-brand-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
        Add Brand
      </button>}
    >
      <div className="max-w-7xl mx-auto space-y-5">
        {showAdd && (
          <div className="bg-white rounded-2xl border border-brand-200 shadow-card p-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Brand name</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Bentley"
                className="w-full px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Country</label>
              <select value={newCountry} onChange={e => setNewCountry(e.target.value)}
                className="px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                {Object.keys(COUNTRY_FLAGS).map(c => <option key={c} value={c}>{COUNTRY_FLAGS[c]} {c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">Category</label>
              <select value={newCategory} onChange={e => setNewCategory(e.target.value)}
                className="px-3 py-2.5 rounded-xl border border-surface-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                {['Passenger', 'SUV', 'Truck', 'Luxury', 'Commercial'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <button onClick={handleAdd} disabled={saving || !newName.trim()}
              className="bg-emerald-600 text-white font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors">
              {saving ? '...' : 'Save'}
            </button>
          </div>
        )}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>}

        <div className="relative max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search brands, country, category..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
        </div>

        {loading ? (
          <p className="text-slate-400 text-sm text-center py-10">Loading...</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {filtered.map((b, i) => (
              <div key={b.id} className="group bg-white rounded-2xl border border-surface-200 shadow-card hover:shadow-card-hover hover:border-brand-200 transition-all p-4 relative">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient(i)} flex items-center justify-center text-white font-bold text-sm shadow-lg mb-3`}>
                  {b.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex items-center justify-between gap-1">
                  <p className="font-semibold text-slate-900 text-sm truncate">{b.name}</p>
                  {b.country && <span className="text-base shrink-0" title={b.country}>{COUNTRY_FLAGS[b.country] ?? '🌍'}</span>}
                </div>
                <div className="flex items-center gap-1.5 mt-2">
                  {b.category && (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${CATEGORY_STYLES[b.category] ?? CATEGORY_STYLES.Passenger}`}>{b.category}</span>
                  )}
                  {!b.is_active && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-red-50 text-red-500 border-red-200">HIDDEN</span>}
                </div>
                <div className="absolute top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleDelete(b.id, b.name)} disabled={deleting === b.id}
                    className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && filtered.length === 0 && <p className="text-slate-400 text-sm text-center py-8">No brands found.</p>}
      </div>
    </Layout>
  )
}
